import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, includes, lowerCase, eq, head } from 'lodash';
import { AppState } from 'app/app.reducer';
import * as actions from 'redux/actions';
import { ConfirmationService } from 'primeng/api';
import { AuthenticationService, ProjectService } from 'app/services';
import { ToastNotificationService } from 'app/components';
import { Notification, Project } from 'app/models';

@Component({
  selector: 'app-select-project',
  templateUrl: './select-project.component.html',
  styles: [
    `
      :host ::ng-deep .p-dialog .p-dialog-header {
        border-bottom: 1px solid #e0dcdc !important;
      }

      :host ::ng-deep .p-dialog .p-dialog-content {
        padding-top: 10px;
      }

      .box-button-left {
        display: flex;
        flex-wrap: wrap;
        row-gap: 0.5rem;
        align-items: center;
      }

      .project-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
        justify-content: space-between;
      }

      .hidden {
        display: none;
      }

      .create {
        margin-top: 0 !important;
      }

      :host ::ng-deep {
        .p-button:focus {
          box-shadow: none;
        }

        .p-inputtext:focus {
          box-shadow: none;
        }

        .p-button {
          background: #465675;
          border-color: transparent;
        }

        .p-button:hover {
          background: #4e5f80;
          border-color: #4e5f80;
        }

        .p-confirm-dialog-reject.p-button {
          background: transparent;
          border-color: transparent;
          color: #465675;
        }

        .p-confirm-dialog-reject.p-button:hover {
          background: rgba(70, 86, 117, 0.04);
          border-color: transparent;
          color: #465675;
        }
      }

      .openDw {
        width: 100%;
        height: 350px;
      }

      .closeDw {
        width: 100%;
        height: 100px;
      }

      .project-option {
        display: grid;
        grid-template-columns: 3rem minmax(0, 1fr) minmax(10rem, auto);
        column-gap: 0.5rem;
        align-items: center;
        width: 100%;
      }

      .project-option-logo-slot {
        align-items: center;
        display: flex;
        height: 50px;
        justify-content: center;
        width: 3rem;
      }

      .project-option-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .project-option-caption {
        color: #6c757d;
        font-size: 0.85rem;
        justify-self: end;
        min-width: 0;
        overflow: hidden;
        text-align: right;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .project-option-logo {
        display: block;
        max-height: 50px;
        max-width: 50px;
        object-fit: contain;
      }

      .my-projects-filter {
        align-items: center;
        display: flex;
        justify-content: flex-end;
      }
    `
  ],
  providers: [ConfirmationService]
})
export class SelectProjectComponent implements OnInit {
  public showModal: boolean;
  public isOpenProjectDw: boolean;
  public projects: Project[];
  public projectList: Project[];
  public projectSelected: Project;
  public showCreateProject: boolean;
  public showEditProject: boolean;
  public userId: any;
  public accountId: any;
  public title: string;
  public nextProjectSelected: Project;
  public disableNewProject: boolean;
  public showMyProjectsOnly: boolean;
  public transferProject: Project = null;
  @ViewChild('searchProject') searchProject: ElementRef;
  @ViewChild('projectNameInput') projectNameInput: ElementRef;

  constructor(
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private projectService: ProjectService,
    private authService: AuthenticationService,
    private notificationService: ToastNotificationService
  ) {
    this.projects = [];
    this.projectList = [];
    this.projectSelected = null;
    this.showCreateProject = false;
    this.showEditProject = false;
    this.userId = null;
    this.accountId = null;
    this.title = 'Context Management';
    this.isOpenProjectDw = false;
    this.disableNewProject = false;
    this.showMyProjectsOnly = false;
  }

  ngOnInit(): void {
    this.store.select('userData').subscribe(({ data }) => {
      if (data) {
        this.userId = data.userId;
        this.accountId = data.accountId;
      }
    });

    this.store
      .select('ui')
      .subscribe(
        ({
          showSelectProjectModal,
          showProjectSettingstSection,
          showProjectCreateSection
        }) => {
          this.showModal = showSelectProjectModal;
          this.showEditProject = showProjectSettingstSection;
          this.showCreateProject = showProjectCreateSection;
          if (showSelectProjectModal) {
            this.title = 'Context Management';
            this.applyProjectFilters();
          }

          if (showProjectSettingstSection) {
            this.title = 'Edit Context';
          }

          if (showProjectCreateSection) {
            this.title = 'Create Context';
          }
        }
      );

    this.store.select('projects').subscribe(({ projects, action }) => {
      if (!!projects.length) {
        this.projectList = projects;
        this.applyProjectFilters();
        this.projectSelected = projects.find(item => item.selected);
      }

      if (eq(action, 'deleteProjectSuccess')) {
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification(
              'Context has been deleted',
              'success',
              'tl'
            )
          })
        );
        this.handleSelectNextProject();
      }

      if (eq(action, 'deleteProjectError')) {
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification('Error deleting context', 'error', 'tl')
          })
        );
      }
    });
  }

  public handleCreateProject() {
    if (this.projectList.length >= parseInt(this.authService.getMaxProjects(), 0)) {
      this.notificationService.showMessage(
        'Contact Sales in order to add more Contexts for your account',
        'Warn',
        10000
      );
      this.disableNewProject = true;
      return;
    }

    this.isOpenProjectDw = false;
    this.store.dispatch(actions.showProjectCreateSection());
  }

  public openProjectSettings() {
    this.isOpenProjectDw = false;
    this.store.dispatch(actions.showProjectSettingsSection());
  }

  public openTransferProject(): void {
    this.isOpenProjectDw = false;
    this.transferProject = this.projectSelected;
  }

  public handleProjectTransferred(project: Project): void {
    this.store.dispatch(actions.setProjectsSuccess({
      projectId: this.projectSelected.value,
      projects: this.projectList.map(item => item.value === project.value ? project : item)
    }));
    if (this.projectSelected.value === project.value) {
      this.projectService.saveProjec(project);
      this.store.dispatch(actions.projectChange({ projectSelected: project }));
    }
    this.notificationService.showMessage('Context ownership has been transferred.', 'Success');
  }

  public handleCancelProject() {
    this.showCreateProject = false;
  }

  public handleSelectNextProject() {
    if (!!this.nextProjectSelected) {
      this.store.dispatch(
        actions.projectChange({
          projectSelected: this.nextProjectSelected
        })
      );

      this.projectService.saveProjec({
        ...this.nextProjectSelected
      });
      this.store.dispatch(
        actions.projectChange({
          projectSelected: this.nextProjectSelected
        })
      );
    } else {
      this.handleCreateProject();
    }
  }

  showDwOptions() {
    this.isOpenProjectDw = true;
  }

  closeDwOptions() {
    this.isOpenProjectDw = false;
  }

  public getProjectCaption(project: Project): string {
    if (!project) {
      return '';
    }

    if (String(project.accountId) === String(this.accountId)) {
      if (String(project.userId) === String(this.userId)) {
        // My project
        return project.shared ? 'Shared' : 'Private';
      } else {
        // My account's project
        return `Shared by ${project.userName}`;
      }
    } else {
      return `Transferred to ${project.userName} @ ${project.accountName}`;
    }
  }

  public getProjectLogo(projectId: number): string {
    return this.projectService.getProjectLogo(projectId, false);
  }
  public onImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    // transparent 1×1 image
    image.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  }

  public handleShowMyProjectsOnlyChange() {
    this.applyProjectFilters();
  }

  private applyProjectFilters(searchValue: string = '') {
    this.projects = filter(this.projectList, item => {
      const matchesSearch = includes(
        lowerCase(item.name),
        lowerCase(searchValue)
      );
      const matchesOwner =
        !this.showMyProjectsOnly ||
        String(item.userId) === String(this.userId);

      return matchesSearch && matchesOwner;
    });
  }

  public selectOneItem({ value }) {
    const projectSelected = { ...value, selected: true };
    this.projectService.saveProjec(projectSelected);
    this.store.dispatch(
      actions.setProjectsSuccess({
        projectId: value.value,
        projects: this.projectList.map(
          item =>
            new Project({
              ...item,
              selected: false
            })
        )
      })
    );
    this.store.dispatch(actions.startProjectChanged());
    this.store.dispatch(actions.projectChange({ projectSelected }));
    this.store.dispatch(actions.loadAuthorsOutlets({ projectId: value.value }));
    // this.onClose();
  }

  public handleSelecProject(project) {
    const projectSelected = { ...project, selected: true };
    this.projectService.saveProjec(projectSelected);
    this.store.dispatch(
      actions.setProjectsSuccess({
        projectId: project.value,
        projects: this.projectList.map(
          item =>
            new Project({
              ...item,
              selected: false
            })
        )
      })
    );
    this.store.dispatch(actions.startProjectChanged());
    this.store.dispatch(actions.projectChange({ projectSelected }));
    this.store.dispatch(
      actions.loadAuthorsOutlets({ projectId: project.value })
    );
    this.onClose();
  }

  public handleSearch(value) {
    this.applyProjectFilters(value);
  }

  deleteProject() {
    this.confirmationService.confirm({
      message: 'Are you sure?',
      header: 'Delete project',
      icon: 'pi pi-exclamation-triangle',
      key: 'deleteProjectConfirmation',
      accept: () => {
        const nextProject = head(
          filter(
            this.projectList,
            item =>
              !eq(lowerCase(item.name), lowerCase(this.projectSelected.name))
          )
        );

        this.nextProjectSelected = { ...nextProject, selected: true };
        this.store.dispatch(
          actions.deleteProject({
            project: this.projectSelected,
            nextProject: this.nextProjectSelected
          })
        );
      },
      reject: type => {
        // handle rejected option ConfirmEventType.CANCEL
      }
    });
  }

  public clearInput() {
    this.applyProjectFilters();
    this.searchProject.nativeElement.value = '';
    this.searchProject.nativeElement.focus();
  }

  public onClose() {
    this.isOpenProjectDw = false;
    this.store.dispatch(actions.hideSelectProjectModal());
    this.store.dispatch(actions.hideProjectCreateSection());
    this.store.dispatch(actions.hideProjectSettingsSection());
  }

  isAdmin() {
    return this.authService.isAdminUser();
  }
}
